import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";

import { FormManager } from "../../../../../components/Form/FormManager";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ConfirmationModal } from "../../../../../components/Modal/ConfirmationModal";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import { showSuccessNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import { usePeriodicCampaignLoadQuery, usePeriodicCampaignUpdateMutation } from "../../services/server/PeriodicCampaignApi";
import type { PeriodicCampaignEntity } from "../../types/PeriodicCampaignEntity";

export const PeriodicCampaignEditForm: FC<FormProps> = ({ runSubmit }) => {
    const navigate = useNavigate();
    const id = "";

    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("periodic-campaign");

    const [formData, setFormData] = useState<PeriodicCampaignEntity>({} as PeriodicCampaignEntity);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [confirmedSubmit, setConfirmedSubmit] = useState(0);
    const previousRunSubmit = useRef(runSubmit);
    const { data: loadFormData, isFetching: loadFormIsInProgress } = usePeriodicCampaignLoadQuery({ entityId: id });
    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
        }
    }, [loadFormData, setFormData]);

    const onChangeCallback = useCallback(setFormData, [setFormData]);

    useEffect(() => {
        if (runSubmit !== previousRunSubmit.current) {
            previousRunSubmit.current = runSubmit;
            setIsConfirmModalOpen(true);
        }
    }, [runSubmit]);

    const onConfirmChange = useCallback(() => {
        setIsConfirmModalOpen(false);
        setConfirmedSubmit((prev) => prev + 1);
    }, []);

    const onCancelChange = useCallback(() => {
        setIsConfirmModalOpen(false);
        if (loadFormData) {
            setFormData(loadFormData);
        }
    }, [loadFormData]);

    const [saveFormData, { isLoading: saveFormIsInProgress }] = usePeriodicCampaignUpdateMutation();
    const dispatch = useDispatch();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formUpdateSuccessNoId", {
                            resource: "PeriodicCampaign",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(`${BASE_PATH}/${ResourceName.PERIODIC_CAMPAIGN}`);
            }
        },
        [saveFormData, formData, navigate, dispatch],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<PeriodicCampaignEntity>
                    name="PeriodicCampaignEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={confirmedSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
            <ConfirmationModal
                visible={isConfirmModalOpen}
                onOk={onConfirmChange}
                onCancel={onCancelChange}
                title={i18n.t("periodicCampaign.Confirmation")}
                message={i18n.t("periodicCampaign.editConfirmation")}
            />
        </Spin>
    );
};
